import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useQzTray } from '../context/QzTrayContext';
import { printLaserSheet } from '../utils/qzTrayService';
import Modal from './Modal';
import './PrintJobsPage.css';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'processing', label: 'Processing' },
  { value: 'printed', label: 'Printed' },
  { value: 'failed', label: 'Failed' },
];

const JOB_FORMAT_ORDER = ['add_stock', 'return', 'restock_line', 'mark_used', 'batch_receive', 'new_product', 'stock_audit'];

const PRINT_ACTION_OPTIONS = [
  { value: 'add_stock', label: 'Add Stock' },
  { value: 'return', label: 'Return' },
  { value: 'restock_line', label: 'Restock Line' },
  { value: 'mark_used', label: 'Mark Used' },
  { value: 'batch_receive', label: 'Batch Receive' },
  { value: 'new_product', label: 'New Product' },
  { value: 'stock_audit', label: 'Stock Audit' },
];

const FALLBACK_PRINT_STICKER_STYLE = {
  labelWidth: 406,
  labelHeight: 203,
  marginX: 16,
  line1Y: 18,
  line2Y: 58,
  line3Y: 96,
  line1FontSize: 30,
  line2FontSize: 26,
  line3FontSize: 30,
  textAlign: 'left',
  textCase: 'as_typed',
  barcodeY: 132,
  barcodeHeight: 60,
  barcodeModuleWidth: 2,
  barcodeAlign: 'left',
  showBarcodeText: true,
  orientation: 'normal',
};

const FALLBACK_LASER_SHEET_LAYOUT = {
  sheetType: 'ST_33_A4',
  label: 'ST-33 A4',
  columns: 3,
  rows: 11,
  labelsPerSheet: 33,
  pageWidthMm: 210,
  pageHeightMm: 297,
  marginLeftMm: 6,
  marginTopMm: 8.8,
  labelWidthMm: 66,
  labelHeightMm: 25.4,
  pitchXmm: 66,
  pitchYmm: 25.4,
};

const FALLBACK_LASER_SHEET_STATE = {
  nextSlot: 1,
};

const CSS_PX_PER_MM = 96 / 25.4;

const FALLBACK_PRINT_JOB_FORMATS = {
  add_stock: {
    label: 'Add Stock',
    template: 'PIECE_2X1',
    line1: '{pieceId}',
    line2: '{sku}',
    line3: '{qty}m',
    barcodeText: '{pieceId}',
    htmlTemplate: '',
  },
  return: {
    label: 'Return',
    template: 'PIECE_2X1',
    line1: '{orderDisplayId}',
    line2: '{sku}',
    line3: '{qty}m',
    barcodeText: '{orderDisplayId}',
    htmlTemplate: '',
  },
  restock_line: {
    label: 'Restock Line',
    template: 'PIECE_2X1',
    line1: '{pieceId}',
    line2: '{sku}',
    line3: '{qty}m',
    barcodeText: '{pieceId}',
    htmlTemplate: '',
  },
  mark_used: {
    label: 'Mark Used',
    template: 'ORDER_SLIP_2X1',
    line1: '{orderDisplayId}',
    line2: '{sku}',
    line3: '{qty}m',
    barcodeText: '{orderDisplayId}',
    htmlTemplate: '',
  },
  batch_receive: {
    label: 'Batch Receive',
    template: 'ORDER_SLIP_2X1',
    line1: '{orderDisplayId}',
    line2: '{sku}',
    line3: '{qty}m',
    barcodeText: '{orderDisplayId}',
    htmlTemplate: '',
  },
  new_product: {
    label: 'New Product',
    template: 'PIECE_2X1',
    line1: '{sku}',
    line2: '{pieceId} • {qty}m',
    line3: '',
    barcodeText: '{sku}',
    htmlTemplate: '',
  },
  stock_audit: {
    label: 'Stock Audit',
    template: 'PIECE_2X1',
    line1: 'PIECE: {pieceId}  LOC: {location}',
    line2: '{sku}',
    line3: '{qty}m  [{auditDate}]',
    barcodeText: '{pieceId}',
    htmlTemplate: '',
  },
};

const JOB_FORMAT_SAMPLES = {
  add_stock: { pieceId: 'FVS-10024-A', sku: 'JS-FLORAL-RED', qty: '5' },
  return: { pieceId: 'SO-729104-B', orderNumber: 'SO-729104-B', orderBaseNumber: 'SO-729104', orderDisplayId: 'SO-729104-B', sku: 'CRP-BLUE-21', qty: '2.5' },
  restock_line: { pieceId: 'R-729104-1', orderNumber: '729104', orderBaseNumber: '729104', orderDisplayId: 'R-729104-1', sku: 'GGT-BLK-09', qty: '3' },
  mark_used: { orderDisplayId: '#729104', pieceId: 'FVS-10024-A', sku: 'SAT-PINK-14', qty: '4' },
  batch_receive: { batchId: 'BATCH-82', batchNumber: 'BATCH-82', orderDisplayId: '#729105', sku: 'LIN-GRN-04', qty: '6' },
  new_product: { sku: 'CRP-BLUE-21', pieceId: 'SO-729104-B', qty: '2.5', designNumber: 'D-104', batchNumber: 'BATCH-82' },
  stock_audit: { pieceId: '104', sku: 'FAB-COT-001-BLUE', qty: '24.5', location: 'RACK-B2', auditBatchName: 'Oct 2026 Audit', auditDate: '02/10', auditedBy: 'staff' },
};

const JOB_FORMAT_FIELDS = {
  add_stock: ['pieceId', 'sku', 'qty'],
  return: ['pieceId', 'orderNumber', 'orderDisplayId', 'orderBaseNumber', 'sku', 'qty'],
  restock_line: ['pieceId', 'orderNumber', 'orderDisplayId', 'orderBaseNumber', 'sku', 'qty', 'restockType'],
  mark_used: ['orderDisplayId', 'pieceId', 'sku', 'qty', 'lineItemId'],
  batch_receive: ['batchId', 'batchNumber', 'orderDisplayId', 'sku', 'qty', 'lineItemId'],
  new_product: ['sku', 'pieceId', 'qty', 'designNumber', 'batchNumber'],
  stock_audit: ['pieceId', 'sku', 'qty', 'location', 'auditBatchName', 'auditBatchId', 'auditDate', 'auditedBy'],
};

function normalizePrintJobFormats(input, defaults = FALLBACK_PRINT_JOB_FORMATS) {
  return JOB_FORMAT_ORDER.reduce((acc, key) => {
    acc[key] = {
      ...(defaults[key] || FALLBACK_PRINT_JOB_FORMATS[key]),
      ...((input && input[key]) || {}),
    };
    return acc;
  }, {});
}

function normalizePrintStickerStyle(input, defaults = FALLBACK_PRINT_STICKER_STYLE) {
  return { ...defaults, ...((input && typeof input === 'object') ? input : {}) };
}

function normalizeLaserSheetLayout(input, defaults = FALLBACK_LASER_SHEET_LAYOUT) {
  const merged = { ...defaults, ...((input && typeof input === 'object') ? input : {}) };
  const columns = Math.max(1, Number(merged.columns || defaults.columns));
  const rows = Math.max(1, Number(merged.rows || defaults.rows));
  return { ...merged, columns, rows, labelsPerSheet: columns * rows };
}

function normalizeLaserSheetState(input, layout = FALLBACK_LASER_SHEET_LAYOUT) {
  const maxSlot = Math.max(1, Number(layout.labelsPerSheet || FALLBACK_LASER_SHEET_LAYOUT.labelsPerSheet));
  const nextSlotRaw = Number(input?.nextSlot || 1);
  const nextSlot = Number.isFinite(nextSlotRaw) ? Math.min(maxSlot, Math.max(1, Math.round(nextSlotRaw))) : 1;
  return { nextSlot };
}

function renderFormatValue(value, sample) {
  return String(value || '').replace(/\{([a-zA-Z0-9_]+)\}/g, (_match, key) => {
    const next = sample?.[key];
    return next == null ? '' : String(next);
  });
}

function applyTextCase(value, style) {
  const text = String(value || '');
  return style?.textCase === 'uppercase' ? text.toUpperCase() : text;
}

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function cleanSeparators(str) {
  return String(str || '')
    .replace(/\s*•\s*•+/g, ' • ')
    .replace(/^(\s*•\s*)+/g, '')
    .replace(/(\s*•\s*)+$/g, '')
    .replace(/\s+•\s+/g, ' • ')
    .trim();
}

function renderFormatPayload(format, sample, style = FALLBACK_PRINT_STICKER_STYLE) {
  const payload = {
    line1: cleanSeparators(applyTextCase(renderFormatValue(format?.line1, sample), style)).slice(0, 28),
    line2: cleanSeparators(applyTextCase(renderFormatValue(format?.line2, sample), style)).slice(0, 32),
    line3: cleanSeparators(applyTextCase(renderFormatValue(format?.line3, sample), style)).slice(0, 24),
    barcodeText: applyTextCase(renderFormatValue(format?.barcodeText, sample), style).slice(0, 48).trim(),
  };
  const templateData = {
    ...(sample || {}),
    line1: payload.line1,
    line2: payload.line2,
    line3: payload.line3,
    barcode: payload.barcodeText,
    barcodeText: payload.barcodeText,
  };
  return {
    ...payload,
    htmlTemplate: format?.htmlTemplate
      ? renderFormatValue(format.htmlTemplate, templateData)
      : '',
  };
}

function sanitizeTemplateClassName(value) {
  return String(value || '')
    .split(/\s+/)
    .map((part) => part.trim().toLowerCase())
    .filter((part) => /^[a-z0-9_-]{1,32}$/.test(part))
    .slice(0, 6)
    .join(' ');
}

function renderControlledPreviewHtml(template, fallbackBarcodeText) {
  const raw = String(template || '').trim();
  if (!raw || typeof window === 'undefined' || typeof window.DOMParser !== 'function') return '';
  const normalized = raw.replace(/<barcode\b([^>]*)\/>/gi, '<barcode$1></barcode>');
  const doc = new window.DOMParser().parseFromString(`<div>${normalized}</div>`, 'text/html');
  const allowed = new Set(['DIV', 'P', 'SPAN', 'STRONG', 'B', 'EM', 'BR', 'BARCODE']);
  const walk = (node) => {
    if (!node) return '';
    if (node.nodeType === window.Node.TEXT_NODE) return escapeHtml(node.textContent || '');
    if (node.nodeType !== window.Node.ELEMENT_NODE) return '';
    if (!allowed.has(node.tagName)) return Array.from(node.childNodes || []).map(walk).join('');
    if (node.tagName === 'BR') return '<br />';
    if (node.tagName === 'BARCODE') {
      const value = node.getAttribute('value') || node.textContent || fallbackBarcodeText || '';
      return `<div class="template-barcode" title="${escapeHtml(value)}"></div>`;
    }
    const tag = node.tagName === 'P' ? 'div' : node.tagName.toLowerCase();
    const className = sanitizeTemplateClassName(node.getAttribute('class'));
    const classAttr = className ? ` class="${escapeHtml(className)}"` : '';
    const body = Array.from(node.childNodes || []).map(walk).join('');
    return `<${tag}${classAttr}>${body}</${tag}>`;
  };
  return Array.from(doc.body.firstElementChild?.childNodes || []).map(walk).join('');
}

function mmFromDots(value, axisDots, axisMm) {
  const n = Number(value || 0);
  const dots = Math.max(1, Number(axisDots || 1));
  const mm = Number(axisMm || 0);
  return Number(((n / dots) * mm).toFixed(3));
}

function ptFromDots(value, labelHeightDots, labelHeightMm, fallbackPt) {
  const n = Number(value || 0);
  if (!Number.isFinite(n) || n <= 0) return fallbackPt;
  return Number((mmFromDots(n, labelHeightDots, labelHeightMm) * 2.83465).toFixed(2));
}

function fitPreviewFontPt(value, basePt) {
  const length = String(value || '').trim().length;
  if (length > 34) return Math.max(5, Number((basePt * 0.62).toFixed(2)));
  if (length > 28) return Math.max(5.4, Number((basePt * 0.72).toFixed(2)));
  if (length > 22) return Math.max(5.8, Number((basePt * 0.82).toFixed(2)));
  return basePt;
}

function laserPreviewLineStyle(value, style, layout, yField, fontField) {
  const safeStyle = normalizePrintStickerStyle(style);
  const safeLayout = normalizeLaserSheetLayout(layout);
  const leftMm = mmFromDots(safeStyle.marginX, safeStyle.labelWidth, safeLayout.labelWidthMm);
  const topMm = mmFromDots(safeStyle[yField], safeStyle.labelHeight, safeLayout.labelHeightMm);
  const widthMm = Math.max(5, safeLayout.labelWidthMm - (leftMm * 2));
  const basePt = ptFromDots(safeStyle[fontField], safeStyle.labelHeight, safeLayout.labelHeightMm, 8);
  return {
    left: `${leftMm}mm`,
    top: `${topMm}mm`,
    width: `${widthMm}mm`,
    fontSize: `${fitPreviewFontPt(value, basePt)}pt`,
    textAlign: safeStyle.textAlign || 'left',
  };
}

function laserPreviewBarcodeStyle(value, style, layout) {
  const safeStyle = normalizePrintStickerStyle(style);
  const safeLayout = normalizeLaserSheetLayout(layout);
  const text = applyTextCase(value, safeStyle);
  const marginMm = mmFromDots(safeStyle.marginX, safeStyle.labelWidth, safeLayout.labelWidthMm);
  const contentWidthMm = Math.max(12, safeLayout.labelWidthMm - (marginMm * 2));
  const estimatedWidthMm = Math.min(
    contentWidthMm,
    Math.max(14, mmFromDots(String(text || '').length * Number(safeStyle.barcodeModuleWidth || 2) * 11, safeStyle.labelWidth, safeLayout.labelWidthMm))
  );
  let leftMm = marginMm;
  if (safeStyle.barcodeAlign === 'center') leftMm = Math.max(0, (safeLayout.labelWidthMm - estimatedWidthMm) / 2);
  if (safeStyle.barcodeAlign === 'right') leftMm = Math.max(0, safeLayout.labelWidthMm - marginMm - estimatedWidthMm);
  const topMm = mmFromDots(safeStyle.barcodeY, safeStyle.labelHeight, safeLayout.labelHeightMm);
  const heightMm = Math.max(3, mmFromDots(safeStyle.barcodeHeight, safeStyle.labelHeight, safeLayout.labelHeightMm));
  return {
    left: `${Number(leftMm.toFixed(3))}mm`,
    top: `${topMm}mm`,
    width: `${Number(estimatedWidthMm.toFixed(3))}mm`,
    height: `${heightMm}mm`,
  };
}

function LaserStickerPreview({ payload, style, layout, scale = 3.2 }) {
  const safeLayout = normalizeLaserSheetLayout(layout);
  const safeStyle = normalizePrintStickerStyle(style);
  const previewPayload = {
    line1: applyTextCase(payload?.line1 || '', safeStyle),
    line2: applyTextCase(payload?.line2 || '', safeStyle),
    line3: applyTextCase(payload?.line3 || '', safeStyle),
    barcodeText: applyTextCase(payload?.barcodeText || '', safeStyle),
    htmlTemplate: payload?.htmlTemplate || '',
  };
  const templateHtml = renderControlledPreviewHtml(previewPayload.htmlTemplate, previewPayload.barcodeText);
  const shellWidthPx = safeLayout.labelWidthMm * CSS_PX_PER_MM * scale;
  const shellHeightPx = safeLayout.labelHeightMm * CSS_PX_PER_MM * scale;
  const previewTransform = safeStyle.orientation === 'inverted'
    ? `scale(${scale}) rotate(180deg) translate(-100%, -100%)`
    : `scale(${scale})`;
  return (
    <div
      className="pj-laser-label-preview-shell"
      style={{
        width: `${shellWidthPx}px`,
        height: `${shellHeightPx}px`,
      }}
    >
      <div
        className={`pj-laser-label-preview ${safeStyle.orientation === 'inverted' ? 'inverted' : ''}`}
        style={{
          width: `${safeLayout.labelWidthMm}mm`,
          height: `${safeLayout.labelHeightMm}mm`,
          transform: previewTransform,
        }}
      >
        {templateHtml ? (
          <div
            className="pj-laser-preview-html-template"
            dangerouslySetInnerHTML={{ __html: templateHtml }}
          />
        ) : (
          <>
            <div className="pj-laser-preview-line" style={laserPreviewLineStyle(previewPayload.line1, safeStyle, safeLayout, 'line1Y', 'line1FontSize')}>
              {previewPayload.line1 || '\u00a0'}
            </div>
            <div className="pj-laser-preview-line" style={laserPreviewLineStyle(previewPayload.line2, safeStyle, safeLayout, 'line2Y', 'line2FontSize')}>
              {previewPayload.line2 || '\u00a0'}
            </div>
            <div className="pj-laser-preview-line" style={laserPreviewLineStyle(previewPayload.line3, safeStyle, safeLayout, 'line3Y', 'line3FontSize')}>
              {previewPayload.line3 || '\u00a0'}
            </div>
            <div className="pj-laser-preview-bars" style={laserPreviewBarcodeStyle(previewPayload.barcodeText, safeStyle, safeLayout)} />
          </>
        )}
      </div>
    </div>
  );
}

function formatTs(ts) {
  if (!ts) return '—';
  try {
    const d = typeof ts?.toDate === 'function' ? ts.toDate() : new Date(ts);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString();
  } catch (_) {
    return '—';
  }
}

function safeText(value, max = 120) {
  if (value == null) return '';
  const s = String(value);
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

export default function PrintJobsPage() {
  const { currentUser, canPerformAction } = useAuth();
  const canManage = canPerformAction('admin.printJobs');
  const apiBase =
    typeof window !== 'undefined' && window.location.hostname === 'localhost'
      ? 'http://localhost:3001'
      : '';

  const [settings, setSettings] = useState({ envEnabled: false, enabled: false });
  const [styleDefaults, setStyleDefaults] = useState(FALLBACK_PRINT_STICKER_STYLE);
  const [printStickerStyle, setPrintStickerStyle] = useState(() => normalizePrintStickerStyle());
  const [laserSheetDefaults, setLaserSheetDefaults] = useState(FALLBACK_LASER_SHEET_LAYOUT);
  const [laserSheetLayout, setLaserSheetLayout] = useState(() => normalizeLaserSheetLayout());
  const [laserSheetState, setLaserSheetState] = useState(FALLBACK_LASER_SHEET_STATE);
  const [pendingJobs, setPendingJobs] = useState([]);
  const [laserActionFilters, setLaserActionFilters] = useState(() => PRINT_ACTION_OPTIONS.map((o) => o.value));
  const [alignmentPreviewJobType, setAlignmentPreviewJobType] = useState('add_stock');
  const [formatPreviewScaleMode, setFormatPreviewScaleMode] = useState('zoom');
  const [printingSheet, setPrintingSheet] = useState(false);
  const [savingSheet, setSavingSheet] = useState(false);
  const [lastLaserBatch, setLastLaserBatch] = useState(null);
  const [formatDefaults, setFormatDefaults] = useState(FALLBACK_PRINT_JOB_FORMATS);
  const [printJobFormats, setPrintJobFormats] = useState(() => normalizePrintJobFormats());
  const [statusFilter, setStatusFilter] = useState('all');
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingStyle, setSavingStyle] = useState(false);
  const [savingFormats, setSavingFormats] = useState(false);
  const [error, setError] = useState('');

  const [showHelp, setShowHelp] = useState(false);

  // ── QZ Tray ──
  const qz = useQzTray() || {};
  const [printerList, setPrinterList] = useState([]);
  const [loadingPrinters, setLoadingPrinters] = useState(false);
  const [testPrinting, setTestPrinting] = useState(false);

  const refreshPrinters = async () => {
    setLoadingPrinters(true);
    try {
      const list = await qz.listPrinters();
      setPrinterList(Array.isArray(list) ? list : []);
    } catch {
      setPrinterList([]);
    } finally {
      setLoadingPrinters(false);
    }
  };

  const handleTestPrint = async () => {
    setTestPrinting(true);
    try {
      await qz.printSticker({
        line1: 'TEST PRINT',
        line2: new Date().toLocaleString(),
        line3: 'QZ Tray OK',
        barcodeText: 'TEST',
        style: printStickerStyle,
      });
    } finally {
      setTestPrinting(false);
    }
  };

  const handleApiResponse = useCallback(async (response, context) => {
    if (!response.ok) {
      let errorMessage = `${context} failed with status ${response.status}.`;
      const contentType = response.headers.get('content-type') || '';
      const errorText = await response.text();
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorMessage;
      } catch (e) {
        const looksLikeHtml = contentType.includes('text/html') || /^\s*<!doctype/i.test(errorText);
        if (looksLikeHtml) {
          errorMessage = `Server returned HTML instead of JSON for ${context}. This usually means the API route isn’t reaching the backend (functions not deployed) or the local backend/proxy isn’t running. Status ${response.status}.`;
        } else {
          errorMessage = `Server returned an invalid response for ${context}: ${errorText.substring(0, 150)}...`;
        }
      }
      throw new Error(errorMessage);
    }
    return response.json();
  }, []);

  const fetchSettings = useCallback(async () => {
    if (!currentUser) return;
    setError('');
    const token = await currentUser.getIdToken();
    const resp = await fetch(`${apiBase}/api/v1/admin/print-jobs/settings`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await handleApiResponse(resp, 'fetch print settings');
    const next = {
      envEnabled: !!data.envEnabled,
      enabled: !!data.enabled,
    };
    setSettings(next);
    const styleDefaultsNext = normalizePrintStickerStyle(data.printStickerStyleDefaults || FALLBACK_PRINT_STICKER_STYLE);
    setStyleDefaults(styleDefaultsNext);
    setPrintStickerStyle(normalizePrintStickerStyle(data.printStickerStyle || {}, styleDefaultsNext));
    const sheetDefaultsNext = normalizeLaserSheetLayout(data.laserSheetLayoutDefaults || FALLBACK_LASER_SHEET_LAYOUT);
    const sheetLayoutNext = normalizeLaserSheetLayout(data.laserSheetLayout || {}, sheetDefaultsNext);
    setLaserSheetDefaults(sheetDefaultsNext);
    setLaserSheetLayout(sheetLayoutNext);
    setLaserSheetState(normalizeLaserSheetState(data.laserSheetState || {}, sheetLayoutNext));
    const defaults = normalizePrintJobFormats(data.printJobFormatDefaults || FALLBACK_PRINT_JOB_FORMATS);
    setFormatDefaults(defaults);
    setPrintJobFormats(normalizePrintJobFormats(data.printJobFormats || {}, defaults));
  }, [apiBase, currentUser, handleApiResponse]);

  const fetchJobs = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    setError('');
    try {
      const token = await currentUser.getIdToken();
      const resp = await fetch(
        `${apiBase}/api/v1/admin/print-jobs?status=${encodeURIComponent(statusFilter)}&limit=80`,
        {
        headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await handleApiResponse(resp, 'fetch print jobs');
      setJobs(Array.isArray(data.jobs) ? data.jobs : []);
    } catch (e) {
      setError(e?.message || 'Failed to fetch print jobs.');
    } finally {
      setLoading(false);
    }
  }, [apiBase, currentUser, handleApiResponse, statusFilter]);

  const fetchPendingJobs = useCallback(async () => {
    if (!currentUser) return;
    try {
      const token = await currentUser.getIdToken();
      const actions = laserActionFilters.join(',');
      const resp = await fetch(`${apiBase}/api/v1/admin/print-jobs?status=pending&printerTarget=STICKER&limit=200&actions=${encodeURIComponent(actions)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await handleApiResponse(resp, 'fetch pending print jobs');
      setPendingJobs(Array.isArray(data.jobs) ? data.jobs : []);
    } catch (e) {
      setError(e?.message || 'Failed to fetch pending print jobs.');
    }
  }, [apiBase, currentUser, handleApiResponse, laserActionFilters]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  useEffect(() => {
    fetchPendingJobs();
  }, [fetchPendingJobs]);

  const toggleEnabled = async () => {
    if (!currentUser) return;
    if (!canManage) return;
    setSaving(true);
    setError('');
    try {
      const token = await currentUser.getIdToken();
      const next = !settings.enabled;
      const resp = await fetch(`${apiBase}/api/v1/admin/print-jobs/settings`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: next }),
      });
      const data = await handleApiResponse(resp, 'update print settings');
      setSettings((prev) => ({
        ...prev,
        enabled: !!data.enabled,
        envEnabled: !!data.envEnabled,
      }));
    } catch (e) {
      setError(e?.message || 'Failed to update print settings.');
    } finally {
      setSaving(false);
    }
  };

  const updatePrintJobFormat = (jobType, field, value) => {
    setPrintJobFormats((prev) => ({
      ...prev,
      [jobType]: {
        ...(prev[jobType] || formatDefaults[jobType] || {}),
        [field]: value,
      },
    }));
  };

  const updatePrintStickerStyle = (field, value) => {
    setPrintStickerStyle((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const updatePrintStickerStyleNumber = (field, value) => {
    updatePrintStickerStyle(field, value === '' ? '' : Number(value));
  };

  const resetPrintStickerStyle = () => {
    setPrintStickerStyle({ ...styleDefaults });
  };

  const savePrintStickerStyle = async () => {
    if (!currentUser || !canManage) return;
    setSavingStyle(true);
    setError('');
    try {
      const token = await currentUser.getIdToken();
      const resp = await fetch(`${apiBase}/api/v1/admin/print-jobs/settings`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ printStickerStyle }),
      });
      const data = await handleApiResponse(resp, 'save sticker style');
      if (data.printStickerStyle) {
        setPrintStickerStyle(normalizePrintStickerStyle(data.printStickerStyle, styleDefaults));
      }
    } catch (e) {
      setError(e?.message || 'Failed to save sticker style.');
    } finally {
      setSavingStyle(false);
    }
  };

  const updateLaserSheetLayout = (field, value) => {
    setLaserSheetLayout((prev) => normalizeLaserSheetLayout({
      ...prev,
      [field]: value === '' ? '' : Number(value),
    }, laserSheetDefaults));
  };

  const updateLaserNextSlot = (value) => {
    setLaserSheetState(normalizeLaserSheetState({ nextSlot: value }, laserSheetLayout));
  };

  const toggleLaserActionFilter = (action) => {
    setLaserActionFilters((prev) => {
      if (prev.includes(action)) {
        return prev.filter((x) => x !== action);
      }
      return [...prev, action];
    });
  };

  const saveLaserSheetSettings = async (nextState = laserSheetState) => {
    if (!currentUser || !canManage) return null;
    setSavingSheet(true);
    setError('');
    try {
      const token = await currentUser.getIdToken();
      const resp = await fetch(`${apiBase}/api/v1/admin/print-jobs/settings`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ laserSheetLayout, laserSheetState: nextState }),
      });
      const data = await handleApiResponse(resp, 'save laser sheet settings');
      const layout = normalizeLaserSheetLayout(data.laserSheetLayout || laserSheetLayout, laserSheetDefaults);
      const state = normalizeLaserSheetState(data.laserSheetState || nextState, layout);
      setLaserSheetLayout(layout);
      setLaserSheetState(state);
      return { layout, state };
    } catch (e) {
      setError(e?.message || 'Failed to save laser sheet settings.');
      return null;
    } finally {
      setSavingSheet(false);
    }
  };

  const laserCapacity = Math.max(0, Number(laserSheetLayout.labelsPerSheet || 33) - Number(laserSheetState.nextSlot || 1) + 1);
  const laserJobsToPrint = pendingJobs.slice(0, laserCapacity);
  const alignmentPreviewFormat =
    printJobFormats[alignmentPreviewJobType] ||
    formatDefaults[alignmentPreviewJobType] ||
    FALLBACK_PRINT_JOB_FORMATS[alignmentPreviewJobType];
  const alignmentPreviewPayload = renderFormatPayload(
    alignmentPreviewFormat,
    JOB_FORMAT_SAMPLES[alignmentPreviewJobType],
    printStickerStyle
  );
  const laserNextSlotAfterPrint = (() => {
    if (laserJobsToPrint.length === 0) return laserSheetState.nextSlot || 1;
    const raw = Number(laserSheetState.nextSlot || 1) + laserJobsToPrint.length;
    return raw > Number(laserSheetLayout.labelsPerSheet || 33) ? 1 : raw;
  })();

  const printPendingLaserSheet = async () => {
    if (laserJobsToPrint.length === 0) return;
    setPrintingSheet(true);
    setError('');
    try {
      printLaserSheet({
        jobs: laserJobsToPrint,
        layout: laserSheetLayout,
        startSlot: laserSheetState.nextSlot,
      });
      setLastLaserBatch({
        jobIds: laserJobsToPrint.map((j) => j.id),
        count: laserJobsToPrint.length,
        startSlot: laserSheetState.nextSlot,
        nextSlot: laserNextSlotAfterPrint,
      });
    } catch (e) {
      setError(e?.message || 'Failed to open laser sheet print.');
    } finally {
      setPrintingSheet(false);
    }
  };

  const confirmLaserSheetPrinted = async () => {
    if (!currentUser || !lastLaserBatch?.jobIds?.length) return;
    setSavingSheet(true);
    setError('');
    try {
      const token = await currentUser.getIdToken();
      const resp = await fetch(`${apiBase}/api/v1/admin/print-jobs/laser-sheet/complete`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ jobIds: lastLaserBatch.jobIds, nextSlot: lastLaserBatch.nextSlot }),
      });
      const data = await handleApiResponse(resp, 'confirm laser sheet print');
      setLaserSheetState(normalizeLaserSheetState({ nextSlot: data.nextSlot }, laserSheetLayout));
      setLastLaserBatch(null);
      await fetchPendingJobs();
      await fetchJobs();
    } catch (e) {
      setError(e?.message || 'Failed to confirm laser sheet print.');
    } finally {
      setSavingSheet(false);
    }
  };

  const resetLaserSheetToFirstSlot = async () => {
    const next = { nextSlot: 1 };
    setLaserSheetState(next);
    await saveLaserSheetSettings(next);
  };

  const resetPrintJobFormat = (jobType) => {
    setPrintJobFormats((prev) => ({
      ...prev,
      [jobType]: { ...(formatDefaults[jobType] || FALLBACK_PRINT_JOB_FORMATS[jobType]) },
    }));
  };

  const savePrintJobFormats = async () => {
    if (!currentUser || !canManage) return;
    setSavingFormats(true);
    setError('');
    try {
      const token = await currentUser.getIdToken();
      const resp = await fetch(`${apiBase}/api/v1/admin/print-jobs/settings`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ printJobFormats }),
      });
      const data = await handleApiResponse(resp, 'save print job formats');
      if (data.printJobFormats) {
        const responseFormats = normalizePrintJobFormats(data.printJobFormats, formatDefaults);
        const mergedFormats = normalizePrintJobFormats(printJobFormats, responseFormats);
        setPrintJobFormats(mergedFormats);
      }
    } catch (e) {
      setError(e?.message || 'Failed to save print job formats.');
    } finally {
      setSavingFormats(false);
    }
  };

  const testPrintJobFormat = async (jobType) => {
    const payload = renderFormatPayload(printJobFormats[jobType], JOB_FORMAT_SAMPLES[jobType], printStickerStyle);
    await qz.printSticker({ ...payload, style: printStickerStyle });
  };


  const derivedCounts = useMemo(() => {
    const c = { pending: 0, processing: 0, printed: 0, failed: 0 };
    jobs.forEach((j) => {
      const s = String(j?.status || '').toLowerCase();
      if (s in c) c[s] += 1;
    });
    return c;
  }, [jobs]);

  return (
    <div className="print-jobs-page">
      <div className="print-jobs-header">
        <div>
          <h1>Print Jobs</h1>
          <p className="print-jobs-subtitle">
            Controls auto-printing and shows recent print activity from the queue.
          </p>
        </div>
        <div className="print-jobs-header-actions">
          <button type="button" className="pj-btn" onClick={() => setShowHelp(true)}>
            Setup help
          </button>
          <button type="button" className="pj-btn" onClick={() => { fetchSettings(); fetchJobs(); }} disabled={loading || saving}>
            Refresh
          </button>
        </div>
      </div>

      {error && <div className="pj-alert error">{error}</div>}

      <div className="pj-card">
        <div className="pj-card-title">QZ Tray (Browser Printing)</div>
        <div className="pj-row pj-row-wrap" style={{ gap: '12px', marginBottom: 10 }}>
          <div className="pj-kv">
            <div className="pj-k">Connection</div>
            <div className="pj-v">
              <span className={`pj-pill ${qz.connected ? 'on' : 'off'}`}>
                {qz.connecting ? 'Connecting...' : qz.connected ? 'Connected' : 'Disconnected'}
              </span>
            </div>
          </div>
          <button
            type="button"
            className={`pj-btn ${qz.connected ? '' : 'primary'}`}
            onClick={() => (qz.connected ? qz.disconnect() : qz.connect().catch(() => {}))}
            disabled={qz.connecting}
          >
            {qz.connected ? 'Disconnect' : 'Connect'}
          </button>
        </div>

        {qz.error && <div className="pj-alert warn" style={{ marginBottom: 10 }}>{qz.error}</div>}

        <div className="pj-row pj-row-wrap" style={{ gap: '12px', marginBottom: 10 }}>
          <label className="pj-filter" style={{ alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={!!qz.config?.enabled}
              onChange={(e) => qz.updateConfig({ enabled: e.target.checked })}
              style={{ marginRight: 4 }}
            />
            <span style={{ color: '#0f172a' }}>Enable QZ Tray on this workstation</span>
          </label>
          <label className="pj-filter" style={{ alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={!!qz.config?.autoConnect}
              onChange={(e) => qz.updateConfig({ autoConnect: e.target.checked })}
              style={{ marginRight: 4 }}
            />
            <span style={{ color: '#0f172a' }}>Auto-connect on page load</span>
          </label>
        </div>

        <div className="pj-row pj-row-wrap" style={{ gap: '12px' }}>
          <label className="pj-filter">
            <span>Printer Type</span>
            <select
              value={qz.config?.printerMode || 'STICKER'}
              onChange={(e) => qz.updateConfig({ printerMode: e.target.value })}
            >
              <option value="STICKER">Sticker printer</option>
              <option value="LASER">Laser printer</option>
            </select>
          </label>
          <label className="pj-filter">
            <span>Sticker Printer</span>
            <select
              value={qz.config?.printers?.STICKER || ''}
              onChange={(e) => qz.updateConfig({ printers: { STICKER: e.target.value } })}
              disabled={qz.config?.printerMode === 'LASER'}
            >
              <option value="">— Select printer —</option>
              {printerList.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
              {qz.config?.printers?.STICKER && !printerList.includes(qz.config.printers.STICKER) && (
                <option value={qz.config.printers.STICKER}>{qz.config.printers.STICKER} (saved)</option>
              )}
            </select>
          </label>
          <button type="button" className="pj-btn" onClick={refreshPrinters} disabled={loadingPrinters || !qz.connected}>
            {loadingPrinters ? 'Loading...' : 'Refresh Printers'}
          </button>
          <button
            type="button"
            className="pj-btn primary"
            onClick={handleTestPrint}
            disabled={
              testPrinting ||
              !qz.config?.enabled ||
              ((qz.config?.printerMode || 'STICKER') === 'STICKER' && !qz.config?.printers?.STICKER)
            }
          >
            {testPrinting ? 'Printing...' : 'Test Print'}
          </button>
        </div>
        <div className="pj-muted" style={{ marginTop: 8 }}>
          QZ Tray config is saved per workstation (browser localStorage). Laser mode opens a browser print window; sticker mode sends raw ZPL through QZ Tray.
        </div>
        {(qz.config?.printerMode || 'STICKER') === 'STICKER' ? (
          <div className="pj-alert warn" style={{ marginTop: 10, marginBottom: 0 }}>
            Select the Zebra/ZPL label printer queue. If ZPL commands print as text, the selected printer or driver is not accepting raw ZPL.
          </div>
        ) : (
          <div className="pj-alert warn" style={{ marginTop: 10, marginBottom: 0 }}>
            Laser mode is for paper testing only. Production auto-print jobs still require a Zebra/ZPL sticker printer.
          </div>
        )}
      </div>

      <div className="pj-card">
        <div className="pj-card-title">Auto print</div>
        {!settings.envEnabled ? (
          <div className="pj-alert warn">
            Auto-print is disabled by server environment. Set `PRINT_JOBS_ENABLED=true` in backend env and redeploy/restart.
          </div>
        ) : null}

        <div className="pj-row">
          <div className="pj-kv">
            <div className="pj-k">Status</div>
            <div className="pj-v">
              <span className={`pj-pill ${settings.enabled ? 'on' : 'off'}`}>
                {settings.enabled ? 'ON' : 'OFF'}
              </span>
              <span className="pj-muted">
                {settings.enabled ? 'Print Station can claim jobs.' : 'Jobs will remain pending until enabled.'}
              </span>
            </div>
          </div>

          <button
            type="button"
            className={`pj-btn ${settings.enabled ? 'danger' : 'primary'}`}
            onClick={toggleEnabled}
            disabled={!settings.envEnabled || !canManage || saving}
            title={!canManage ? 'You do not have permission to change this setting.' : undefined}
          >
            {saving ? 'Saving…' : settings.enabled ? 'Turn OFF' : 'Turn ON'}
          </button>
        </div>
      </div>

      <div className="pj-card">
        <div className="pj-format-header">
          <div>
            <div className="pj-card-title">Laser sheet printing</div>
            <div className="pj-muted">
              Manual A4 sheet printing for ST-33 labels. Default is 33 labels per A4, 3 x 11, 66 x 25.4 mm.
            </div>
          </div>
          <div className="pj-format-actions">
            <button type="button" className="pj-btn" onClick={() => { fetchPendingJobs(); fetchSettings(); }} disabled={savingSheet}>
              Refresh
            </button>
            <button type="button" className="pj-btn" onClick={resetLaserSheetToFirstSlot} disabled={!canManage || savingSheet}>
              Reset to slot 1
            </button>
            <button type="button" className="pj-btn" onClick={() => saveLaserSheetSettings()} disabled={!canManage || savingSheet}>
              Save sheet
            </button>
          </div>
        </div>

        <div className="pj-laser-summary">
          <div className="pj-kv">
            <div className="pj-k">Pending sticker jobs</div>
            <div className="pj-v">{pendingJobs.length}</div>
          </div>
          <label className="pj-format-field pj-slot-field">
            <span>Start slot</span>
            <input
              type="number"
              min="1"
              max={laserSheetLayout.labelsPerSheet || 33}
              value={laserSheetState.nextSlot || 1}
              onChange={(e) => updateLaserNextSlot(e.target.value)}
              disabled={!canManage}
            />
          </label>
          <div className="pj-kv">
            <div className="pj-k">This print</div>
            <div className="pj-v">{laserJobsToPrint.length} labels</div>
          </div>
          <div className="pj-kv">
            <div className="pj-k">Next slot after print</div>
            <div className="pj-v">{laserNextSlotAfterPrint}</div>
          </div>
        </div>

        <div className="pj-action-filter-row">
          <span className="pj-action-filter-title">Job types</span>
          {PRINT_ACTION_OPTIONS.map((option) => (
            <label className="pj-action-filter" key={option.value}>
              <input
                type="checkbox"
                checked={laserActionFilters.includes(option.value)}
                onChange={() => toggleLaserActionFilter(option.value)}
              />
              <span>{option.label}</span>
            </label>
          ))}
          <button type="button" className="pj-btn" onClick={() => setLaserActionFilters(PRINT_ACTION_OPTIONS.map((o) => o.value))}>
            All
          </button>
          <button type="button" className="pj-btn" onClick={() => setLaserActionFilters([])}>
            None
          </button>
        </div>

        <div className="pj-laser-actions">
          <button
            type="button"
            className="pj-btn primary"
            onClick={printPendingLaserSheet}
            disabled={printingSheet || laserJobsToPrint.length === 0}
          >
            {printingSheet ? 'Opening...' : `Print ${laserJobsToPrint.length} pending labels`}
          </button>
          {lastLaserBatch ? (
            <>
              <button type="button" className="pj-btn primary" onClick={confirmLaserSheetPrinted} disabled={!canManage || savingSheet}>
                Confirm printed
              </button>
              <button type="button" className="pj-btn" onClick={() => setLastLaserBatch(null)} disabled={savingSheet}>
                Not printed
              </button>
              <span className="pj-muted">
                Last opened: {lastLaserBatch.count} labels from slot {lastLaserBatch.startSlot}. Confirm only after paper is correct.
              </span>
            </>
          ) : null}
        </div>

        <div className="pj-alert warn" style={{ marginTop: 12 }}>
          Browser print must be set to A4, scale 100%, margins none/default minimum. Confirm only after checking alignment.
        </div>

        <details className="pj-sheet-details">
          <summary>Sheet alignment settings</summary>
          <div className="pj-style-grid" style={{ marginTop: 12 }}>
            {[
              ['columns', 'Columns'],
              ['rows', 'Rows'],
              ['pageWidthMm', 'Page width mm'],
              ['pageHeightMm', 'Page height mm'],
              ['marginLeftMm', 'Left margin mm'],
              ['marginTopMm', 'Top margin mm'],
              ['labelWidthMm', 'Label width mm'],
              ['labelHeightMm', 'Label height mm'],
              ['pitchXmm', 'Pitch X mm'],
              ['pitchYmm', 'Pitch Y mm'],
            ].map(([field, label]) => (
              <label className="pj-format-field" key={field}>
                <span>{label}</span>
                <input
                  type="number"
                  step="0.1"
                  value={laserSheetLayout[field] ?? ''}
                  onChange={(e) => updateLaserSheetLayout(field, e.target.value)}
                  disabled={!canManage}
                />
              </label>
            ))}
          </div>

          <div className="pj-alignment-preview">
            <div className="pj-alignment-preview-head">
              <div>
                <div className="pj-subtitle">Sticker preview</div>
                <div className="pj-muted">
                  Uses saved placeholders for the selected job type and the same HTML positioning as laser sheet print.
                </div>
              </div>
              <label className="pj-format-field compact">
                <span>Job type</span>
                <select
                  value={alignmentPreviewJobType}
                  onChange={(e) => setAlignmentPreviewJobType(e.target.value)}
                >
                  {JOB_FORMAT_ORDER.map((jobType) => {
                    const format = printJobFormats[jobType] || formatDefaults[jobType] || FALLBACK_PRINT_JOB_FORMATS[jobType];
                    return (
                      <option key={jobType} value={jobType}>
                        {format.label || jobType}
                      </option>
                    );
                  })}
                </select>
              </label>
            </div>

            <div className="pj-alignment-preview-body">
              <LaserStickerPreview
                payload={alignmentPreviewPayload}
                style={printStickerStyle}
                layout={laserSheetLayout}
                scale={3.2}
              />
              <div className="pj-preview-readout">
                <div><span>Line 1</span><strong>{alignmentPreviewPayload.line1 || '-'}</strong></div>
                <div><span>Line 2</span><strong>{alignmentPreviewPayload.line2 || '-'}</strong></div>
                <div><span>Line 3</span><strong>{alignmentPreviewPayload.line3 || '-'}</strong></div>
                <div><span>Barcode</span><strong>{alignmentPreviewPayload.barcodeText || '-'}</strong></div>
              </div>
            </div>
          </div>
        </details>

        <div className="pj-sheet-preview" aria-label="ST-33 sheet slot preview">
          {Array.from({ length: Number(laserSheetLayout.labelsPerSheet || 33) }).map((_, index) => {
            const slot = index + 1;
            const isStart = slot === Number(laserSheetState.nextSlot || 1);
            const isUsedThisRun = slot >= Number(laserSheetState.nextSlot || 1) && slot < Number(laserSheetState.nextSlot || 1) + laserJobsToPrint.length;
            return (
              <div key={slot} className={`pj-sheet-slot ${isStart ? 'start' : ''} ${isUsedThisRun ? 'fill' : ''}`}>
                {slot}
              </div>
            );
          })}
        </div>
      </div>

      <div className="pj-card">
        <div className="pj-format-header">
          <div>
            <div className="pj-card-title">Universal sticker style</div>
            <div className="pj-muted">
              Applies to every sticker job. Content fields below decide what text is printed.
            </div>
          </div>
          <div className="pj-format-actions">
            <button type="button" className="pj-btn" onClick={resetPrintStickerStyle} disabled={!canManage}>
              Reset style
            </button>
            <button
              type="button"
              className="pj-btn primary"
              onClick={savePrintStickerStyle}
              disabled={!canManage || savingStyle}
              title={!canManage ? 'You do not have permission to change style.' : undefined}
            >
              {savingStyle ? 'Saving...' : 'Save style'}
            </button>
          </div>
        </div>

        <div className="pj-style-grid">
          {[
            ['labelWidth', 'Label width'],
            ['labelHeight', 'Label height'],
            ['marginX', 'Margin X'],
            ['line1Y', 'Line 1 Y'],
            ['line2Y', 'Line 2 Y'],
            ['line3Y', 'Line 3 Y'],
            ['line1FontSize', 'Line 1 font'],
            ['line2FontSize', 'Line 2 font'],
            ['line3FontSize', 'Line 3 font'],
            ['barcodeY', 'Barcode Y'],
            ['barcodeHeight', 'Barcode height'],
            ['barcodeModuleWidth', 'Barcode width'],
          ].map(([field, label]) => (
            <label className="pj-format-field" key={field}>
              <span>{label}</span>
              <input
                type="number"
                value={printStickerStyle[field] ?? ''}
                onChange={(e) => updatePrintStickerStyleNumber(field, e.target.value)}
                disabled={!canManage}
              />
            </label>
          ))}

          <label className="pj-format-field">
            <span>Text align</span>
            <select
              value={printStickerStyle.textAlign || 'left'}
              onChange={(e) => updatePrintStickerStyle('textAlign', e.target.value)}
              disabled={!canManage}
            >
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
            </select>
          </label>

          <label className="pj-format-field">
            <span>Text case</span>
            <select
              value={printStickerStyle.textCase || 'as_typed'}
              onChange={(e) => updatePrintStickerStyle('textCase', e.target.value)}
              disabled={!canManage}
            >
              <option value="as_typed">As typed</option>
              <option value="uppercase">Uppercase</option>
            </select>
          </label>

          <label className="pj-format-field">
            <span>Barcode align</span>
            <select
              value={printStickerStyle.barcodeAlign || 'left'}
              onChange={(e) => updatePrintStickerStyle('barcodeAlign', e.target.value)}
              disabled={!canManage}
            >
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
            </select>
          </label>

          <label className="pj-format-field">
            <span>Orientation</span>
            <select
              value={printStickerStyle.orientation || 'normal'}
              onChange={(e) => updatePrintStickerStyle('orientation', e.target.value)}
              disabled={!canManage}
            >
              <option value="normal">Normal</option>
              <option value="inverted">Inverted</option>
            </select>
          </label>

          <label className="pj-style-checkbox">
            <input
              type="checkbox"
              checked={!!printStickerStyle.showBarcodeText}
              onChange={(e) => updatePrintStickerStyle('showBarcodeText', e.target.checked)}
              disabled={!canManage}
            />
            <span>Show barcode text</span>
          </label>
        </div>
      </div>

      <div className="pj-card">
        <div className="pj-format-header">
          <div>
            <div className="pj-card-title">Sticker formats</div>
            <div className="pj-muted">
              Use placeholders like {'{sku}'}, {'{qty}'}, {'{pieceId}'}, and {'{orderDisplayId}'}. Printed output is still Zebra ZPL on 2x1 inch stickers.
            </div>
          </div>
          <div className="pj-format-actions">
            <div className="pj-preview-mode-toggle" aria-label="Sticker preview scale">
              <button
                type="button"
                className={formatPreviewScaleMode === 'zoom' ? 'active' : ''}
                onClick={() => setFormatPreviewScaleMode('zoom')}
              >
                Zoom preview
              </button>
              <button
                type="button"
                className={formatPreviewScaleMode === 'actual' ? 'active' : ''}
                onClick={() => setFormatPreviewScaleMode('actual')}
              >
                Actual size
              </button>
            </div>
            <button
              type="button"
              className="pj-btn primary"
              onClick={savePrintJobFormats}
              disabled={!canManage || savingFormats}
              title={!canManage ? 'You do not have permission to change formats.' : undefined}
            >
              {savingFormats ? 'Saving...' : 'Save formats'}
            </button>
          </div>
        </div>

        <div className="pj-format-list">
          {JOB_FORMAT_ORDER.map((jobType) => {
            const format = printJobFormats[jobType] || formatDefaults[jobType] || FALLBACK_PRINT_JOB_FORMATS[jobType];
            const preview = renderFormatPayload(format, JOB_FORMAT_SAMPLES[jobType], printStickerStyle);
            return (
              <section className="pj-format-section" key={jobType}>
                <div className="pj-format-title-row">
                  <div>
                    <h3>{format.label}</h3>
                    <div className="pj-muted">{format.template} / {jobType}</div>
                  </div>
                  <div className="pj-format-actions">
                    <button type="button" className="pj-btn" onClick={() => resetPrintJobFormat(jobType)} disabled={!canManage}>
                      Reset
                    </button>
                    <button
                      type="button"
                      className="pj-btn"
                      onClick={() => testPrintJobFormat(jobType)}
                      disabled={
                        !qz.config?.enabled ||
                        ((qz.config?.printerMode || 'STICKER') === 'STICKER' && !qz.config?.printers?.STICKER)
                      }
                    >
                      Test print
                    </button>
                  </div>
                </div>

                <div className="pj-format-fields">
                  {[
                    ['line1', 'Line 1'],
                    ['line2', 'Line 2'],
                    ['line3', 'Line 3'],
                    ['barcodeText', 'Barcode'],
                  ].map(([field, label]) => (
                    <label className="pj-format-field" key={field}>
                      <span>{label}</span>
                      <input
                        type="text"
                        value={format[field] || ''}
                        onChange={(e) => updatePrintJobFormat(jobType, field, e.target.value)}
                        disabled={!canManage}
                        maxLength={160}
                      />
                    </label>
                  ))}
                </div>

                <details className="pj-advanced-template">
                  <summary>Advanced HTML template</summary>
                  <div className="pj-muted">
                    Use {'{line1}'}, {'{line2}'}, {'{line3}'}, and {'{barcode}'}. Sizes: fs-6/7/8/9/10/11/12/14/16/18. Spacing: pt-1/2/3, pb-1/2/3, mt-1/2/3, mb-1/2/3, spacer-1/2/3.
                  </div>
                  <textarea
                    value={format.htmlTemplate || ''}
                    onChange={(e) => updatePrintJobFormat(jobType, 'htmlTemplate', e.target.value)}
                    disabled={!canManage}
                    maxLength={2000}
                    rows={5}
                    placeholder={`<div class="spacer-1"></div>\n<div class="line fs-10 fw-950 lh-10 center">{line1}</div>\n<div class="line fs-8 fw-800 lh-10 center">{line2}</div>\n<div class="line fs-9 fw-900 lh-10 center">{line3}</div>\n<barcode value="{barcode}" />\n<div class="spacer-1"></div>`}
                  />
                  <div className="pj-format-actions">
                    <button
                      type="button"
                      className="pj-btn"
                      disabled={!canManage}
                      onClick={() => updatePrintJobFormat(
                        jobType,
                        'htmlTemplate',
                        `<div class="line fs-10 fw-950 lh-10 center">{line1}</div>\n<div class="line fs-8 fw-800 lh-10 center">{line2}</div>\n<div class="line fs-9 fw-900 lh-10 center">{line3}</div>\n<barcode value="{barcode}" />`
                      )}
                    >
                      Use line tokens
                    </button>
                    <button
                      type="button"
                      className="pj-btn"
                      disabled={!canManage}
                      onClick={() => updatePrintJobFormat(
                        jobType,
                        'htmlTemplate',
                        `<div class="line fs-10 fw-950 lh-10 center">{line1}</div>\n<div class="row-center lh-10">\n  <span class="line fs-8 fw-800">{line2}</span>\n  <span class="line fs-8 fw-900">{line3}</span>\n</div>\n<barcode value="{barcode}" />`
                      )}
                    >
                      Line 2 + 3 same row
                    </button>
                    <button
                      type="button"
                      className="pj-btn"
                      disabled={!canManage || !format.htmlTemplate}
                      onClick={() => updatePrintJobFormat(jobType, 'htmlTemplate', '')}
                    >
                      Clear advanced
                    </button>
                  </div>
                </details>

                <div className="pj-format-footer">
                  <div className="pj-placeholder-list">
                    {(JOB_FORMAT_FIELDS[jobType] || []).map((field) => (
                      <button
                        type="button"
                        key={field}
                        className="pj-placeholder"
                        onClick={() => navigator.clipboard?.writeText(`{${field}}`).catch(() => {})}
                        title="Click to copy"
                      >
                        {'{'}{field}{'}'}
                      </button>
                    ))}
                  </div>
                  <LaserStickerPreview
                    payload={preview}
                    style={printStickerStyle}
                    layout={laserSheetLayout}
                    scale={formatPreviewScaleMode === 'actual' ? 1 : 3}
                  />
                </div>
              </section>
            );
          })}
        </div>
      </div>

      <div className="pj-card">
        <div className="pj-card-title">Recent jobs</div>
        <div className="pj-row pj-row-wrap">
          <label className="pj-filter">
            <span>Status</span>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <div className="pj-counts">
            <span className="pj-mini">Pending: {derivedCounts.pending}</span>
            <span className="pj-mini">Processing: {derivedCounts.processing}</span>
            <span className="pj-mini">Printed: {derivedCounts.printed}</span>
            <span className="pj-mini">Failed: {derivedCounts.failed}</span>
          </div>
        </div>

        <div className="pj-table-wrap">
          <table className="pj-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Status</th>
                <th>Target</th>
                <th>Template</th>
                <th>Line 1</th>
                <th>Line 2</th>
                <th>Line 3</th>
                <th>Error</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="pj-empty">Loading…</td>
                </tr>
              ) : jobs.length === 0 ? (
                <tr>
                  <td colSpan={8} className="pj-empty">No jobs found.</td>
                </tr>
              ) : (
                jobs.map((j) => {
                  const s = String(j?.status || '').toLowerCase();
                  const payload = j?.payload || {};
                  return (
                    <tr key={j.id}>
                      <td>{formatTs(j.createdAt)}</td>
                      <td>
                        <span className={`pj-pill status ${s}`}>{s || '—'}</span>
                      </td>
                      <td>{safeText(j.printerTarget || '—', 30)}</td>
                      <td>{safeText(j.template || '—', 40)}</td>
                      <td>{safeText(payload.line1 || '', 40)}</td>
                      <td>{safeText(payload.line2 || '', 40)}</td>
                      <td>{safeText(payload.line3 || '', 40)}</td>
                      <td className="pj-error-cell">{safeText(j.error || '', 120)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showHelp && (
        <Modal isOpen={showHelp} onClose={() => setShowHelp(false)}>
          <div className="pj-help">
            <h2>QZ Tray setup</h2>
            <ol>
              <li>Download and install QZ Tray from <a href="https://qz.io/download/" target="_blank" rel="noreferrer">qz.io</a>.</li>
              <li>Start QZ Tray — it runs in the system tray.</li>
              <li>On this page, click Connect, then Refresh Printers.</li>
              <li>Select your sticker printer from the dropdown.</li>
              <li>Click Test Print to verify.</li>
              <li>Enable "Auto-connect on page load" for hands-free operation.</li>
            </ol>
            <div className="pj-help-note">
              QZ Tray config is saved per workstation in your browser. Each computer needs its own setup.
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
